import { z } from "zod";

/**
 * The collab conversation's message vocabulary.
 *
 * A turn between the user and a generation agent has the same shape whatever is
 * being built — the backend serializes it from one `CollabMessage` model — and
 * every field here is a single word, so it survives any router's casing
 * convention unchanged.
 */

export const CollabMessageRoleSchema = z.enum(["user", "assistant"]);
export type CollabMessageRole = z.infer<typeof CollabMessageRoleSchema>;

// A question the agent needs answered before it can safely act. The turn that
// asked changed nothing, so answering (or ignoring it) is free.
export const ClarificationRequestSchema = z.object({
  question: z.string(),
  options: z.array(z.string()).default([]),
});
export type ClarificationRequest = z.infer<typeof ClarificationRequestSchema>;

// Something the user asked for that the agent could not (fully) do, and why.
export const UnappliedRequestSchema = z.object({
  request: z.string(),
  reason: z.string(),
  suggestion: z.string().nullable().optional(),
});
export type UnappliedRequest = z.infer<typeof UnappliedRequestSchema>;

// 'clarification' — the agent needs an answer and changed nothing.
// 'caveat' — the agent applied some or none of the request and says so.
// 'proposal' — the agent proposed changes (as prose in `content`) and changed
// nothing; the user approves in a follow-up chat turn.
export const CollabMessageKindSchema = z.enum([
  "message",
  "clarification",
  "caveat",
  "proposal",
]);
export type CollabMessageKind = z.infer<typeof CollabMessageKindSchema>;

export const CollabMessageSchema = z.object({
  id: z.string(),
  role: CollabMessageRoleSchema,
  content: z.string(),
  timestamp: z.string(),
  kind: CollabMessageKindSchema.optional(),
  clarification: ClarificationRequestSchema.nullable().optional(),
  unapplied: z.array(UnappliedRequestSchema).optional(),
});
export type CollabMessage = z.infer<typeof CollabMessageSchema>;
